import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
    getChartInstance,
    setupResize,
    clearContainer,
    resolveColors,
} from "./utils";

export const renderRadarChart = (
    container: HTMLElement | null,
    data: ChartData,
    config: ChartConfig,
    _size?: ChartSize,
    theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
    if (!container || typeof window === "undefined") return null;

    clearContainer(container);

    // Set container size
    container.style.width = "100%";
    container.style.height = "100%";

    const chartInstance = getChartInstance(container, theme);
    const colors = resolveColors(config, theme);

    // Map data to radar format
    // Radar expects indicators (axes) and data (series)
    const indicatorField = (config.indicatorField as string) || "name";
    const valueFields = (config.valueFields as string[]) || [];

    if (data.length === 0 || valueFields.length === 0) {
        return container;
    }

    // Indicators: use all rows from data, based on indicatorField
    const indicators = data.map(row => {
        return { name: String(row[indicatorField] || "") };
    });

    // Series Data: one series per valueField
    const seriesData = valueFields.map((field, index) => {
        return {
            name: field,
            value: data.map(row => Number(row[field]) || 0),
            itemStyle: {
                color: colors[index % colors.length]
            },
            areaStyle: {
                opacity: 0.3
            }
        };
    });

    const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        title: {
            text: config.title,
            show: false
        },
        legend: {
            show: config.showLegend !== false,
            data: valueFields,
            bottom: 0,
            textStyle: {
                color: theme === 'dark' ? '#ccc' : '#333'
            }
        },
        tooltip: {
            trigger: 'item'
        },
        radar: {
            indicator: indicators,
            axisName: {
                color: theme === 'dark' ? '#aaa' : '#666'
            },
            splitLine: {
                lineStyle: {
                    color: theme === 'dark' ? '#333' : '#eee'
                }
            },
            splitArea: {
                show: false
            },
            axisLine: {
                lineStyle: {
                    color: theme === 'dark' ? '#333' : '#eee'
                }
            }
        },
        series: [
            {
                name: config.title || 'Radar Chart',
                type: 'radar',
                data: seriesData
            }
        ]
    };

    chartInstance.setOption(option);
    setupResize(container, chartInstance);

    return container;
};
